// Pure notebook-structural undo/redo logic (docs/undo_redo_incident_20260922.md §5.2).
// Deliberately has no React/Firebase dependency — App.tsx wires this to state and
// Firestore sync; this module is what gets unit-tested (tests/undoHistory.test.ts).

import { MindMapNode, NodeLink, Position } from '../types';

// Structural-only projection of a node — the fields a move/reparent/reorder can
// change. Deliberately excludes every payload field (content, title, tags, images,
// analysis, YouTube/review/thumbnail data, ...) so that undoing a structural change
// can NEVER revert a content edit made after that snapshot was taken (§2.3/§5.2).
export interface StructuralProjection {
    id: string;
    parentId?: string;
    siblingOrder?: number;
    position: Position;
}

export function toStructuralProjection(nodes: MindMapNode[]): StructuralProjection[] {
    return nodes.map(n => ({ id: n.id, parentId: n.parentId, siblingOrder: n.siblingOrder, position: n.position }));
}

// Merge a structural snapshot onto the CURRENT nodes array: only the 4 structural
// fields are overwritten, per matching id. A node no longer present is skipped (never
// resurrected by structural undo/redo). A node present now but absent from the
// snapshot (created after the snapshot was taken) is left completely untouched.
export function applyStructuralProjection(currentNodes: MindMapNode[], snapshot: StructuralProjection[]): MindMapNode[] {
    const byId = new Map(snapshot.map(s => [s.id, s]));
    return currentNodes.map(n => {
        const s = byId.get(n.id);
        if (!s) return n;
        return { ...n, parentId: s.parentId, siblingOrder: s.siblingOrder, position: s.position };
    });
}

export interface ReparentedChild { id: string; fromParentId?: string; toParentId?: string }

// "Past" entries — commands as originally performed. Undoing one applies its inverse.
export type HistoryEntry =
    | { kind: 'structural'; snapshot: StructuralProjection[] }
    | { kind: 'create'; nodeId: string }
    | { kind: 'delete-single'; deletedNode: MindMapNode; reparentedChildren: ReparentedChild[]; removedLinks: NodeLink[] }
    | { kind: 'delete-subtree'; deletedNodes: MindMapNode[]; removedLinks: NodeLink[] };

// "Future" entries — the redo-side counterpart produced by an undo. Redoing one
// re-applies the original command, after validating (for existence-changing kinds)
// that nothing in its affected set has changed since the undo (§5.2.2).
export type RedoEntry =
    | { kind: 'structural'; snapshot: StructuralProjection[] }
    | { kind: 'recreate'; snapshot: MindMapNode }
    | { kind: 'redelete-single'; deletedNode: MindMapNode; reparentedChildren: ReparentedChild[]; removedLinks: NodeLink[]; affectedIds: string[]; revisionsAtUndo: Record<string, number> }
    | { kind: 'redelete-subtree'; deletedNodes: MindMapNode[]; removedLinks: NodeLink[]; affectedIds: string[]; revisionsAtUndo: Record<string, number> };

// Lightweight "has this node's payload changed" signal — App.tsx's updateNode() already
// bumps updatedAt on every content/title/tags/image edit (never on pure UI-state
// toggles), so it's a ready-made revision counter without inventing new tracking (§5.2.2).
export function nodeRevision(node: MindMapNode | undefined): number {
    return node?.updatedAt ?? 0;
}

export interface NotebookSlice { nodes: MindMapNode[]; links: NodeLink[] }

// NOTE: the discriminant is a single field (`status`) with a UNIQUE literal per
// variant — not a shared `ok: true/false` with a second `reason` field. Without
// `strictNullChecks` (this project's tsconfig doesn't set `strict: true`), TypeScript
// fails to narrow a union where two variants share the same `ok: false` literal (a
// real, reproducible limitation, not a style preference) — `if (!result.ok)` leaves
// `result` typed as the `ok: true` member and any access to a false-only field errors.
// A single all-unique-literal discriminant narrows correctly either way.
export type UndoResult =
    | { status: 'ok'; nodes: MindMapNode[]; links: NodeLink[]; redoEntry: RedoEntry; pendingDeletionChanges: { id: string; pending: boolean }[] }
    | { status: 'no-op' }
    | { status: 'has-children'; nodeId: string };

// Apply the inverse of `entry` to `slice`. Never mutates its input.
export function applyUndo(slice: NotebookSlice, entry: HistoryEntry): UndoResult {
    if (entry.kind === 'structural') {
        const redoEntry: RedoEntry = { kind: 'structural', snapshot: toStructuralProjection(slice.nodes) };
        return { status: 'ok', nodes: applyStructuralProjection(slice.nodes, entry.snapshot), links: slice.links, redoEntry, pendingDeletionChanges: [] };
    }

    if (entry.kind === 'create') {
        const node = slice.nodes.find(n => n.id === entry.nodeId);
        if (!node) return { status: 'no-op' };
        const hasChildren = slice.nodes.some(n => n.parentId === entry.nodeId);
        if (hasChildren) return { status: 'has-children', nodeId: entry.nodeId };
        return {
            status: 'ok',
            nodes: slice.nodes.filter(n => n.id !== entry.nodeId),
            links: slice.links,
            redoEntry: { kind: 'recreate', snapshot: node },
            pendingDeletionChanges: [{ id: entry.nodeId, pending: true }],
        };
    }

    // delete-single / delete-subtree
    const restoredNodes = entry.kind === 'delete-single' ? [entry.deletedNode] : entry.deletedNodes;
    const affectedIds = entry.kind === 'delete-single'
        ? [entry.deletedNode.id, ...entry.reparentedChildren.map(c => c.id)]
        : entry.deletedNodes.map(n => n.id);

    let nodes = [...slice.nodes, ...restoredNodes];
    if (entry.kind === 'delete-single') {
        nodes = nodes.map(n => {
            const rc = entry.reparentedChildren.find(c => c.id === n.id);
            return rc ? { ...n, parentId: rc.fromParentId } : n;
        });
    }
    const revisionsAtUndo: Record<string, number> = {};
    for (const id of affectedIds) revisionsAtUndo[id] = nodeRevision(nodes.find(n => n.id === id));
    const links = [...slice.links, ...entry.removedLinks];

    const redoEntry: RedoEntry = entry.kind === 'delete-single'
        ? { kind: 'redelete-single', deletedNode: entry.deletedNode, reparentedChildren: entry.reparentedChildren, removedLinks: entry.removedLinks, affectedIds, revisionsAtUndo }
        : { kind: 'redelete-subtree', deletedNodes: entry.deletedNodes, removedLinks: entry.removedLinks, affectedIds, revisionsAtUndo };

    return { status: 'ok', nodes, links, redoEntry, pendingDeletionChanges: affectedIds.map(id => ({ id, pending: false })) };
}

export type RedoResult =
    | { status: 'ok'; nodes: MindMapNode[]; links: NodeLink[]; undoEntry: HistoryEntry; pendingDeletionChanges: { id: string; pending: boolean }[] }
    | { status: 'no-op' }
    | { status: 'stale' };

// Apply `entry` forward to `slice`. Never mutates its input.
export function applyRedo(slice: NotebookSlice, entry: RedoEntry): RedoResult {
    if (entry.kind === 'structural') {
        const undoEntry: HistoryEntry = { kind: 'structural', snapshot: toStructuralProjection(slice.nodes) };
        return { status: 'ok', nodes: applyStructuralProjection(slice.nodes, entry.snapshot), links: slice.links, undoEntry, pendingDeletionChanges: [] };
    }

    if (entry.kind === 'recreate') {
        return {
            status: 'ok',
            nodes: [...slice.nodes, entry.snapshot],
            links: slice.links,
            undoEntry: { kind: 'create', nodeId: entry.snapshot.id },
            pendingDeletionChanges: [{ id: entry.snapshot.id, pending: false }],
        };
    }

    // redelete-single / redelete-subtree — §5.2.2: invalidate if anything in the
    // affected set changed since undo, or if a new node was added as a child of
    // anything in the affected set.
    const stillValid = entry.affectedIds.every(id => nodeRevision(slice.nodes.find(n => n.id === id)) === entry.revisionsAtUndo[id])
        && !slice.nodes.some(n => n.parentId && entry.affectedIds.includes(n.parentId) && !entry.affectedIds.includes(n.id));
    if (!stillValid) return { status: 'stale' };

    const idsToRemove = new Set(entry.affectedIds);
    let nodes = slice.nodes.filter(n => !idsToRemove.has(n.id));
    if (entry.kind === 'redelete-single') {
        const grandparentId = entry.deletedNode.parentId;
        nodes = nodes.map(n => entry.reparentedChildren.some(c => c.id === n.id) ? { ...n, parentId: grandparentId } : n);
    }
    const removedLinkIds = new Set(entry.removedLinks.map(l => l.id));
    const links = slice.links.filter(l => !removedLinkIds.has(l.id));

    const undoEntry: HistoryEntry = entry.kind === 'redelete-single'
        ? { kind: 'delete-single', deletedNode: entry.deletedNode, reparentedChildren: entry.reparentedChildren, removedLinks: entry.removedLinks }
        : { kind: 'delete-subtree', deletedNodes: entry.deletedNodes, removedLinks: entry.removedLinks };

    return { status: 'ok', nodes, links, undoEntry, pendingDeletionChanges: entry.affectedIds.map(id => ({ id, pending: true })) };
}
